import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useBranding } from "@/lib/branding";
import { useAuth } from "@/lib/auth";
import PayPalModal from "@/components/PayPalModal";
import {
  ArrowRight,
  ClipboardList,
  GraduationCap,
  Palette,
  ShieldCheck,
  Sparkles,
  Check,
  Timer,
  Wallet,
  Copy,
  X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const WHITELABEL_PRICE = 699;

// Introductory pricing ends Oct 31, 2026 at 11:59 PM Eastern.
// EST offset = UTC-5 (Standard Time — Nov 1 is after DST ends Nov 2, 2025, so Nov 1 is STD).
// But Oct 31, 2026 at 11:59 PM Eastern is still Eastern Daylight Time (EDT = UTC-4).
// DST ends Nov 1, 2026 at 2:00 AM. So Oct 31 11:59 PM is EDT.
const INTRO_END_ISO = "2026-10-31T23:59:00-04:00";

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const target = new Date(targetIso).getTime();
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds, expired: diff === 0 };
}

export default function WhiteLabel() {
  const { branding } = useBranding();
  const { user } = useAuth();
  const countdown = useCountdown(INTRO_END_ISO);
  const [cashOpen, setCashOpen] = useState(false);
  const [paypalOpen, setPaypalOpen] = useState(false);
  const [cashTag, setCashTag] = useState("");
  const [paypalHandle, setPaypalHandle] = useState("Dynamictax80");

  useEffect(() => {
    axios
      .get(`${API}/api/branding`)
      .then((r) => setCashTag(r.data?.cash_app_tag || "$DimekaH"))
      .catch(() => setCashTag("$DimekaH"));
    axios
      .get(`${API}/api/payments/plans`)
      .then((r) => setPaypalHandle(r.data?.paypal_handle || "Dynamictax80"))
      .catch(() => {});
  }, []);

  const cashLink = `https://cash.app/${cashTag.replace(/^\$/, "$")}/${WHITELABEL_PRICE}`;

  const copyTag = async () => {
    try {
      await navigator.clipboard.writeText(cashTag);
      toast.success("Cashtag copied.");
    } catch {
      toast.error("Couldn't copy — long-press to select the tag.");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="whitelabel-page">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">
                {branding.brand_name?.[0]}
              </div>
            )}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Home</Link>
            <Link to="/demo" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Take the tour</Link>
            <Link to="/afsp-bundle" className="hidden sm:inline underline text-accent hover:text-accent/80 font-medium" data-testid="whitelabel-nav-afsp">AFSP Prep</Link>
            <Link to="/pricing" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Pricing</Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(46,79,184,0.35) 0%, rgba(15,23,42,0.98) 55%, #0a0f1f 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20 lg:py-28 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-xs font-medium">
            <Sparkles size={13} className="text-accent" /> Introductory price · Ends soon
          </div>
          <h1
            className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-white mb-6"
            data-testid="whitelabel-hero-title"
          >
            Buy your own
            <br />
            <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-white to-blue-300">
              white-label system.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 mb-3 tracking-wide">
            Your brand. Your clients. Your software.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-4 text-xs text-white/80">
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/20">Branded Intake Forms</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/20">Instructor Classes</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/20">Your Logo &amp; Colors</span>
          </div>

          {/* Price + CTAs */}
          <div className="mt-12 inline-flex items-baseline gap-3">
            <span className="text-white/50 line-through text-2xl">$997</span>
            <span className="font-serif text-6xl md:text-7xl text-white">$699</span>
            <span className="text-sm text-white/60">/ one-time</span>
          </div>
          <div className="mt-2 text-xs text-accent font-medium tracking-wide">
            LIMITED-TIME INTRODUCTORY PRICE
          </div>

          {/* Countdown */}
          {!countdown.expired && (
            <div className="mt-6 inline-flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 border border-white/20 backdrop-blur-sm" data-testid="whitelabel-countdown">
              <Timer size={16} className="text-accent" />
              <div className="text-xs text-white/70 uppercase tracking-wider mr-1">Intro price ends in</div>
              <div className="flex items-baseline gap-2 text-white font-serif">
                <TimeCell n={countdown.days} label="days" />
                <span className="text-white/40">:</span>
                <TimeCell n={countdown.hours} label="hrs" />
                <span className="text-white/40">:</span>
                <TimeCell n={countdown.minutes} label="min" />
                <span className="text-white/40">:</span>
                <TimeCell n={countdown.seconds} label="sec" />
              </div>
            </div>
          )}
          <div className="mt-2 text-[11px] text-white/50">
            Ends Oct 31, 2026 at 11:59 PM Eastern · reverts to $997 after
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button size="lg" className="bg-[#0070ba] hover:bg-[#005ea6] text-white" onClick={() => setPaypalOpen(true)} data-testid="whitelabel-cta-paypal">
              <Wallet size={16} className="mr-2" /> Pay $699 with PayPal
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setCashOpen(true)}
              className="border-white/40 text-white hover:bg-white/10"
              data-testid="whitelabel-cta-cashapp"
            >
              <Wallet size={16} className="mr-2" /> Pay with Cash App
            </Button>
            <Button asChild size="lg" variant="ghost" className="text-white hover:bg-white/10" data-testid="whitelabel-cta-tour">
              <Link to="/demo">Take the tour first</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* SPLIT PREVIEW */}
      <section className="border-t border-border bg-secondary/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="label-caps mb-3 text-primary">Same platform · two brands</div>
            <h2 className="font-serif text-4xl md:text-5xl mb-4">
              Make it look like <span className="italic text-primary">you designed it.</span>
            </h2>
            <p className="text-muted-foreground">
              Every screen — intake forms, course dashboards, client portal, emails, certificates — automatically re-skins with your logo, brand colors, business name, and support email. Your clients will never guess it isn't custom software.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <BrandCard
              tag="Powered by DyNamic Tax"
              logoBg="#2E4FB8"
              logoInitial="D"
              logoTint="text-white"
              accent="#2E4FB8"
              brandName="DyNamic Tax"
              subtitle="Client Intake Form"
              testid="wl-preview-a"
            />
            <BrandCard
              tag="Your Business"
              logoBg="#D68A3D"
              logoInitial="Y"
              logoTint="text-white"
              accent="#D68A3D"
              brandName="Your Tax Business"
              subtitle="Client Intake Form"
              testid="wl-preview-b"
              highlight
            />
          </div>
        </div>
      </section>

      {/* FEATURE CARDS */}
      <section className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="label-caps mb-3 text-primary">What's in the box</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              Everything a modern tax practice needs.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard
              icon={<ClipboardList size={22} />}
              title="Branded Intake Forms"
              body="Client intake, amendments, and document uploads — all rendered with your logo, colors, and business name."
              testid="wl-feature-intake"
            />
            <FeatureCard
              icon={<GraduationCap size={22} />}
              title="Instructor Classes"
              body="Host courses, track student progress, and issue certificates with your name on them. AI teacher included."
              testid="wl-feature-classes"
            />
            <FeatureCard
              icon={<Palette size={22} />}
              title="Your Logo & Colors"
              body="Upload a logo and pick a primary color. Every page, every email, every PDF re-skins automatically. Zero code."
              testid="wl-feature-branding"
            />
            <FeatureCard
              icon={<ShieldCheck size={22} />}
              title="100% Ownership"
              body="Your brand. Your clients. Your data. No 'powered by' footers your customers see unless you want one."
              testid="wl-feature-ownership"
            />
          </div>
        </div>
      </section>

      {/* WHAT YOU GET (checklist) */}
      <section className="border-t border-border bg-secondary/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">What $699 gets you</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              A complete tax practice, delivered.
            </h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {WHATS_INCLUDED.map((item, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`wl-included-${i}`}>
                <div className="mt-0.5 h-5 w-5 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                  <Check size={13} />
                </div>
                <span className="text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">Frequently asked</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              The short version.
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="whitelabel-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`whitelabel-faq-item-${i}`}>
                <AccordionTrigger className="text-left font-serif text-lg">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24 text-center">
          <h2 className="font-serif text-4xl md:text-6xl mb-4">
            Your brand. <span className="italic text-primary">Your clients.</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
            Skip 18 months of building. Launch your branded tax platform this week for a one-time introductory price.
          </p>
          <div className="inline-flex items-baseline gap-3 mb-8">
            <span className="text-muted-foreground line-through text-xl">$997</span>
            <span className="font-serif text-5xl">$699</span>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button size="lg" className="bg-[#0070ba] hover:bg-[#005ea6] text-white" onClick={() => setPaypalOpen(true)} data-testid="whitelabel-final-cta-paypal">
              <Wallet size={16} className="mr-2" /> Pay $699 with PayPal
            </Button>
            <Button size="lg" variant="outline" onClick={() => setCashOpen(true)} data-testid="whitelabel-final-cta-cashapp">
              <Wallet size={16} className="mr-2" /> Pay with Cash App
            </Button>
            <Button asChild size="lg" variant="ghost" data-testid="whitelabel-final-cta-tour">
              <Link to="/demo">Or tour it first</Link>
            </Button>
          </div>
          {!user && (
            <div className="mt-6 text-xs text-muted-foreground">
              Prefer to talk first? <Link to="/book" className="underline text-primary">Book a discovery call →</Link>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
          <div>Powered by <span className="text-foreground">{branding.brand_name}</span></div>
          <div className="flex gap-4">
            <Link to="/" className="underline hover:text-foreground">Home</Link>
            <Link to="/demo" className="underline hover:text-foreground">Demo</Link>
            <Link to="/pricing" className="underline hover:text-foreground">Pricing</Link>
          </div>
        </div>
      </footer>

      {/* PayPal modal (redirects to onboarding once user confirms) */}
      <PayPalModal
        open={paypalOpen}
        onClose={() => setPaypalOpen(false)}
        plan={{ id: "whitelabel", name: "White-Label System", amount: 699 }}
        paypalHandle={paypalHandle}
        user={user}
        redirectAfter="/white-label/onboarding"
      />

      {/* Cash App modal */}
      {cashOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 grid place-items-center p-4"
          onClick={() => setCashOpen(false)}
          data-testid="whitelabel-cashapp-modal"
        >
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
              onClick={() => setCashOpen(false)}
              aria-label="Close"
              data-testid="whitelabel-cashapp-close"
            >
              <X size={18} />
            </button>
            <div className="label-caps mb-2 text-primary">Cash App · White-Label</div>
            <h3 className="font-serif text-3xl mb-4">
              Pay $699 to {cashTag || "$DimekaH"}
            </h3>
            <div className="flex items-center justify-center mb-5">
              <div className="bg-white p-3 rounded-md border border-border">
                <QRCodeSVG
                  value={cashLink}
                  size={168}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="M"
                  imageSettings={{
                    src: "https://cash.app/favicon.ico",
                    height: 28,
                    width: 28,
                    excavate: true,
                  }}
                />
              </div>
            </div>
            <div className="text-center text-xs text-muted-foreground mb-5">
              📱 On your phone? Scan the QR — it opens Cash App with the amount pre-filled to {cashTag || "$DimekaH"}.
            </div>
            <ol className="space-y-2 text-sm mb-5">
              <li>
                <strong>1.</strong> Send <strong>$699</strong> to <strong>{cashTag || "$DimekaH"}</strong>.
              </li>
              <li>
                <strong>2.</strong> Include your email in the Cash App note so we can match the payment.
              </li>
              <li>
                <strong>3.</strong> Sign in or create an account, then fill in your onboarding info. We'll spin up your branded instance within 24-48 hours.
              </li>
            </ol>
            <div className="flex flex-wrap gap-2 mb-5">
              <Button variant="outline" size="sm" onClick={copyTag} data-testid="whitelabel-cashapp-copy">
                <Copy size={14} className="mr-1.5" /> Copy Cashtag
              </Button>
              <Button variant="outline" size="sm" asChild data-testid="whitelabel-cashapp-open">
                <a href={cashLink} target="_blank" rel="noreferrer">
                  <Wallet size={14} className="mr-1.5" /> Open Cash App
                </a>
              </Button>
            </div>
            <Button asChild className="w-full" data-testid="whitelabel-cashapp-onboard">
              <Link to={user ? "/white-label/onboarding" : "/register?next=/white-label/onboarding"}>
                I've paid — set up my instance <ArrowRight size={14} className="ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const TimeCell = ({ n, label }) => (
  <div className="flex flex-col items-center min-w-[42px]">
    <span className="text-2xl md:text-3xl leading-none tabular-nums">{String(n).padStart(2, "0")}</span>
    <span className="text-[9px] uppercase tracking-widest text-white/50 mt-0.5">{label}</span>
  </div>
);

const FAQS = [
  {
    q: "How long does setup take?",
    a: "After you check out, you'll land on a short onboarding wizard (business name, logo, primary color, admin email). Once you submit it, our team spins up your branded instance within 24-48 business hours and emails you your admin login.",
  },
  {
    q: "What's included in the $699?",
    a: "One-time purchase of a fully branded copy of the entire platform — marketing homepage, client intake, amendment intake, client portal, booking calendar with auto-generated Jitsi links, both 13-chapter tax courses (1040 & 1120), AI tax teacher, admin dashboard, and email confirmations. No monthly fees. No per-user fees. Your brand, your clients, your data.",
  },
  {
    q: "Do I need to bring my own domain and hosting?",
    a: "No — we host the app on a custom subdomain by default (e.g. yourbrand.example.com). If you want to bring your own domain, we'll help you point it (one-time DNS setup, no extra cost).",
  },
  {
    q: "What's the refund policy?",
    a: "Because we spin up a dedicated branded instance for each buyer, all sales are final once your onboarding form is submitted. Before that, you can request a 100% refund within 7 days of purchase — just email support and we'll process it.",
  },
  {
    q: "Can I sell courses to my own students?",
    a: "Absolutely. Your instance includes the same instructor tools we use — student management, progress tracking, certificate issuance, and the ability to price and sell courses under your own brand through your own Stripe account.",
  },
  {
    q: "What happens after October 31, 2026?",
    a: "The $699 introductory price ends at 11:59 PM Eastern on October 31, 2026. After that the price reverts to the regular $997. Buyers who lock in the intro price keep it forever — no surprise upsells.",
  },
];

const WHATS_INCLUDED = [
  "Full customer-facing marketing homepage",
  "Client intake with secure document uploads",
  "Amendment intake form (for prior-year returns)",
  "Client portal with return-status tracking",
  "Interactive booking calendar with auto-generated Jitsi video links",
  "Branded email confirmations (Resend integration)",
  "13-chapter Form 1040 course with AI tax teacher",
  "13-chapter Form 1120 (C-Corp) course",
  "Guided practice returns for both courses",
  "Downloadable certificates for students who pass",
  "Admin dashboard: students, intakes, appointments, amendments",
  "Trainable FAQ that powers your AI Welcome Bubble",
];

const FeatureCard = ({ icon, title, body, testid }) => (
  <div
    className="border border-border rounded-md p-6 bg-card hover:border-primary transition-colors"
    data-testid={testid}
  >
    <div className="h-10 w-10 rounded-md bg-primary/10 text-primary grid place-items-center mb-4">
      {icon}
    </div>
    <h3 className="font-serif text-xl mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
  </div>
);

const BrandCard = ({ tag, logoBg, logoInitial, logoTint, accent, brandName, subtitle, testid, highlight }) => (
  <div
    className={`relative rounded-lg border ${highlight ? "border-accent" : "border-border"} bg-card overflow-hidden`}
    data-testid={testid}
  >
    {highlight && (
      <div className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full bg-accent text-accent-foreground text-[10px] font-semibold tracking-wide">
        DESIGNED FOR YOUR BRAND
      </div>
    )}
    {/* Mock top-bar with brand identity */}
    <div className="flex items-center gap-3 border-b border-border p-4">
      <div
        className={`h-9 w-9 rounded-md grid place-items-center font-serif text-lg ${logoTint}`}
        style={{ backgroundColor: logoBg }}
      >
        {logoInitial}
      </div>
      <div>
        <div className="font-serif text-sm leading-tight">{brandName}</div>
        <div className="text-[10px] text-muted-foreground tracking-wide uppercase">{tag}</div>
      </div>
    </div>
    {/* Mock intake form body */}
    <div className="p-5 space-y-3">
      <div className="label-caps text-[10px]">{subtitle}</div>
      <div className="space-y-2">
        {["Full name", "Email address", "Phone number", "Mailing address"].map((label) => (
          <div key={label} className="border border-border rounded px-3 py-2.5 text-xs text-muted-foreground">
            {label}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="w-full mt-2 rounded px-3 py-2.5 text-xs font-medium text-white transition"
        style={{ backgroundColor: accent }}
      >
        Next step →
      </button>
      <div className="mt-3 flex items-center justify-between text-[10px]">
        <span className="text-muted-foreground">Progress</span>
        <span style={{ color: accent }}>75%</span>
      </div>
      <div className="h-1 bg-secondary rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: "75%", backgroundColor: accent }} />
      </div>
    </div>
  </div>
);
